type GapiAuthInstance = {
  attachClickHandler: (
    element: HTMLElement,
    options: Record<string, never>,
    success: (result: {
      getBasicProfile: () => { getEmail: () => string; getId: () => string }
      getAuthResponse: () => { access_token: string }
    }) => void,
    failure: (error: { error?: string }) => void
  ) => void
}

type GapiWindow = Window & {
  gapi?: {
    load: (module: string, callback: () => void) => void
    auth2: {
      getAuthInstance: () => GapiAuthInstance | null
      init: (options: { client_id: string; scope: string }) => Promise<GapiAuthInstance> & GapiAuthInstance
    }
  }
}

let scriptPromise: Promise<void> | null = null
let authPromise: Promise<void> | null = null
let authInstance: GapiAuthInstance | null = null
const boundElements = new WeakSet<HTMLElement>()

export const loadGooglePlatform = (): Promise<void> => {
  if (typeof window === "undefined") return Promise.reject(new Error("Google login requires a browser"))
  const w = window as GapiWindow
  if (w.gapi) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("google-platform-script")
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true })
      existing.addEventListener("error", () => reject(new Error("Load Google script failed")), { once: true })
      return
    }
    const script = document.createElement("script")
    script.id = "google-platform-script"
    script.src = "https://apis.google.com/js/platform.js"
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error("Load Google script failed"))
    document.head.appendChild(script)
  })

  return scriptPromise
}

export const attachGoogleLogin = async (
  element: HTMLElement,
  clientId: string,
  onSuccess: (openid: string, accessToken: string, email: string) => void,
  onError: (message: string) => void
): Promise<void> => {
  if (boundElements.has(element)) return
  await loadGooglePlatform()
  const w = window as GapiWindow
  if (!w.gapi) throw new Error("Google API not loaded")

  authPromise ||= new Promise<void>((resolve, reject) => {
    w.gapi!.load("auth2", () => {
      const existing = w.gapi!.auth2.getAuthInstance()
      if (existing) {
        authInstance = existing
        resolve()
        return
      }
      const auth = w.gapi!.auth2.init({
        client_id: clientId,
        scope: "profile email openid",
      })
      auth.then(() => {
        authInstance = auth
        resolve()
      }, reject)
    })
  })

  await authPromise
  if (!authInstance) throw new Error("Google auth2 init failed")
  boundElements.add(element)
  authInstance.attachClickHandler(
    element,
    {},
    (result) => {
      const profile = result.getBasicProfile()
      onSuccess(profile.getId(), result.getAuthResponse().access_token, profile.getEmail())
    },
    (error) => {
      if (error?.error !== "popup_closed_by_user") {
        onError(`Google sign in failed: ${error?.error || "unknown"}`)
      }
    }
  )
}
